import { throttle } from '../utils/PerformanceUtils.js';

/**
 * GraphManager - Manages the 2D graph visualization of 360° images and their connections
 * Uses Canvas 2D for high-performance rendering
 */
export class GraphManager {
    constructor(canvasElement) {
        this.canvas = canvasElement;
        this.ctx = canvasElement.getContext('2d');

        // Graph data
        this.nodes = []; // {id, imageId, x, y, label, locked}
        this.edges = []; // {from, to}
        this.selectedImageId = null; // Currently selected image ID

        // Viewport
        this.viewportX = 0;
        this.viewportY = 0;
        this.scale = 1.0;
        this.minScale = 0.2;
        this.maxScale = 3.0;

        // Interaction state
        this.isDragging = false;
        this.isPanning = false;
        this.draggedNode = null;
        this.dragStartX = 0;
        this.dragStartY = 0;
        this.panStartX = 0;
        this.panStartY = 0;
        this.hoveredNode = null;
        this.dragThreshold = 5; // Minimum pixels to move before considering it a drag

        // Connection creation mode
        this.isInConnectionMode = false;
        this.connectionSourceNode = null;

        // Node styling
        this.nodeSize = 80; // Size of thumbnail (square)
        this.nodeRadius = 40; // Half of nodeSize for hit detection
        this.nodeColor = '#3b82f6'; // Blue
        this.nodeHoverColor = '#60a5fa';
        this.nodeSelectedColor = '#10b981'; // Green
        this.nodeLabelFont = '11px sans-serif';
        this.nodeLabelColor = '#fff';
        this.nodeBorderColor = '#3b82f6';
        this.nodeBorderHoverColor = '#60a5fa';

        // Edge styling
        this.edgeColor = '#60a5fa'; // Blue for better visibility
        this.edgeWidth = 3;
        this.edgeHoverColor = '#94a3b8';
        this.edgeSelectedColor = '#10b981'; // Green for selected edges

        // Auto-layout settings
        this.nodeSpacingX = 160; // Increased for thumbnails
        this.nodeSpacingY = 160; // Increased for thumbnails + labels

        // Callbacks
        this.onNodeClick = null;
        this.onNodeRightClick = null;
        this.onEdgeRightClick = null; // Callback when user right-clicks an edge
        this.onGraphChanged = null;
        this.onConnectionRequested = null; // Callback when user selects destination node
        this.onConnectionModeChanged = null; // Callback when connection mode state changes

        // Event handlers (stored for cleanup)
        this.boundResizeHandler = null;
        this.boundMouseDownHandler = null;
        this.boundMouseMoveHandler = null;
        this.boundMouseUpHandler = null;
        this.boundMouseLeaveHandler = null;
        this.boundWheelHandler = null;
        this.boundContextMenuHandler = null;

        // Initialize
        this.setupCanvas();
        this.attachEventListeners();
        this.render();
    }

    /**
     * Setup canvas size
     */
    setupCanvas() {
        // Create throttled resize handler (120ms)
        this.boundResizeHandler = throttle(() => this.resizeCanvas(), 120);

        // Initial resize
        this.resizeCanvas();

        // Resize again after a short delay to ensure container is fully rendered
        setTimeout(() => this.resizeCanvas(), 100);

        // Watch for window resize with throttling
        window.addEventListener('resize', this.boundResizeHandler);
    }

    /**
     * Resize canvas to match container
     */
    resizeCanvas() {
        const container = this.canvas.parentElement;
        const rect = container.getBoundingClientRect();

        // Store old dimensions
        const oldWidth = this.canvas.width;
        const oldHeight = this.canvas.height;

        // Set canvas internal resolution to match container
        this.canvas.width = rect.width;
        this.canvas.height = rect.height;

        // If there's a selected image, try to keep it centered
        if (this.selectedImageId && oldWidth > 0 && oldHeight > 0) {
            const selectedNode = this.nodes.find(n => n.imageId === this.selectedImageId);
            if (selectedNode) {
                // Calculate where the selected node was on screen before resize
                const screenPos = this.worldToScreen(selectedNode.x, selectedNode.y);

                // Calculate the difference in canvas size
                const deltaWidth = rect.width - oldWidth;
                const deltaHeight = rect.height - oldHeight;

                // Adjust viewport to keep the node relatively centered
                this.viewportX += deltaWidth / 2;
                this.viewportY += deltaHeight / 2;
            }
        }

        this.render();
    }

    /**
     * Attach event listeners for interaction
     */
    attachEventListeners() {
        // Store bound handlers for cleanup
        this.boundMouseDownHandler = (e) => this.handleMouseDown(e);
        this.boundMouseMoveHandler = (e) => this.handleMouseMove(e);
        this.boundMouseUpHandler = (e) => this.handleMouseUp(e);
        this.boundMouseLeaveHandler = () => this.handleMouseLeave();
        this.boundWheelHandler = (e) => this.handleWheel(e);
        this.boundContextMenuHandler = (e) => this.handleContextMenu(e);

        // Mouse events
        this.canvas.addEventListener('mousedown', this.boundMouseDownHandler);
        this.canvas.addEventListener('mousemove', this.boundMouseMoveHandler);
        this.canvas.addEventListener('mouseup', this.boundMouseUpHandler);
        this.canvas.addEventListener('mouseleave', this.boundMouseLeaveHandler);
        this.canvas.addEventListener('wheel', this.boundWheelHandler);
        this.canvas.addEventListener('contextmenu', this.boundContextMenuHandler);

        console.log('✅ GraphManager event listeners attached');
    }

    /**
     * Convert screen coordinates to world coordinates
     */
    screenToWorld(screenX, screenY) {
        const rect = this.canvas.getBoundingClientRect();
        const x = (screenX - rect.left - this.viewportX) / this.scale;
        const y = (screenY - rect.top - this.viewportY) / this.scale;
        return { x, y };
    }

    /**
     * Convert world coordinates to screen coordinates
     */
    worldToScreen(worldX, worldY) {
        const x = worldX * this.scale + this.viewportX;
        const y = worldY * this.scale + this.viewportY;
        return { x, y };
    }

    /**
     * Find node at screen position
     */
    getNodeAtPosition(screenX, screenY) {
        const world = this.screenToWorld(screenX, screenY);
        const halfSize = this.nodeSize / 2;

        // Check in reverse order to prioritize nodes drawn on top
        for (let i = this.nodes.length - 1; i >= 0; i--) {
            const node = this.nodes[i];

            // Rectangle hit detection (for square thumbnails)
            if (
                world.x >= node.x - halfSize &&
                world.x <= node.x + halfSize &&
                world.y >= node.y - halfSize &&
                world.y <= node.y + halfSize
            ) {
                return node;
            }
        }

        return null;
    }

    /**
     * Find edge at screen position (for right-click)
     * @param {number} screenX - Screen X coordinate
     * @param {number} screenY - Screen Y coordinate
     * @param {number} threshold - Distance threshold in world coordinates (default: 10)
     * @returns {Object|null} Edge object {from, to} or null
     */
    getEdgeAtPosition(screenX, screenY, threshold = 10) {
        const world = this.screenToWorld(screenX, screenY);

        for (const edge of this.edges) {
            const fromNode = this.nodes.find(n => n.id === edge.from);
            const toNode = this.nodes.find(n => n.id === edge.to);

            if (!fromNode || !toNode) continue;

            // Calculate distance from point to line segment
            const distance = this.distanceToLineSegment(
                world.x, world.y,
                fromNode.x, fromNode.y,
                toNode.x, toNode.y
            );

            if (distance < threshold / this.scale) {
                return edge;
            }
        }

        return null;
    }

    /**
     * Calculate distance from a point to a line segment
     * @param {number} px - Point X
     * @param {number} py - Point Y
     * @param {number} x1 - Line start X
     * @param {number} y1 - Line start Y
     * @param {number} x2 - Line end X
     * @param {number} y2 - Line end Y
     * @returns {number} Distance
     */
    distanceToLineSegment(px, py, x1, y1, x2, y2) {
        const A = px - x1;
        const B = py - y1;
        const C = x2 - x1;
        const D = y2 - y1;

        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        let param = -1;

        if (lenSq !== 0) {
            param = dot / lenSq;
        }

        let xx, yy;

        if (param < 0) {
            xx = x1;
            yy = y1;
        } else if (param > 1) {
            xx = x2;
            yy = y2;
        } else {
            xx = x1 + param * C;
            yy = y1 + param * D;
        }

        const dx = px - xx;
        const dy = py - yy;
        return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * Handle mouse down
     */
    handleMouseDown(e) {
        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const node = this.getNodeAtPosition(e.clientX, e.clientY);

        if (node) {
            // Potential drag - store start position (unless in connection mode)
            this.draggedNode = node;
            this.dragStartX = e.clientX;
            this.dragStartY = e.clientY;

            if (!this.isInConnectionMode) {
                this.canvas.style.cursor = 'grabbing';
            }
        } else if (e.button === 0) { // Left click, no node
            // Start panning (unless in connection mode)
            if (!this.isInConnectionMode) {
                this.isPanning = true;
                this.panStartX = e.clientX - this.viewportX;
                this.panStartY = e.clientY - this.viewportY;
                this.canvas.style.cursor = 'grabbing';
            }
        }
    }

    /**
     * Handle mouse move
     */
    handleMouseMove(e) {
        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Check if we've moved enough to consider it a drag
        if (this.draggedNode && !this.isDragging) {
            const dx = e.clientX - this.dragStartX;
            const dy = e.clientY - this.dragStartY;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance > this.dragThreshold) {
                this.isDragging = true;
            }
        }

        if (this.isDragging && this.draggedNode && !this.isInConnectionMode) {
            // Drag node (disabled in connection mode)
            const world = this.screenToWorld(e.clientX, e.clientY);
            this.draggedNode.x = world.x;
            this.draggedNode.y = world.y;
            this.draggedNode.locked = true; // Mark as manually positioned
            this.render();

            if (this.onGraphChanged) {
                this.onGraphChanged();
            }
        } else if (this.isPanning && !this.isInConnectionMode) {
            // Pan viewport
            this.viewportX = e.clientX - this.panStartX;
            this.viewportY = e.clientY - this.panStartY;
            this.render();
        } else {
            // Update hover state
            const node = this.getNodeAtPosition(e.clientX, e.clientY);

            if (node !== this.hoveredNode) {
                this.hoveredNode = node;
                this.canvas.style.cursor = node ? 'pointer' : 'grab';
                this.render();
            }
        }
    }

    /**
     * Handle mouse up
     */
    handleMouseUp(e) {
        if (this.isDragging) {
            // Node was dragged, don't trigger click
            this.isDragging = false;
            this.draggedNode = null;
            this.canvas.style.cursor = this.isInConnectionMode ? 'crosshair' : 'grab';
        } else if (this.draggedNode) {
            // mouseDown on node but didn't move enough = click
            if (this.isInConnectionMode) {
                // In connection mode, handle node selection for connection
                this.handleConnectionNodeClick(this.draggedNode);
            } else if (this.onNodeClick) {
                this.onNodeClick(this.draggedNode);
            }
            this.draggedNode = null;
            this.canvas.style.cursor = this.isInConnectionMode ? 'crosshair' : 'grab';
        } else if (!this.isPanning) {
            // Regular click (not on node, not panning)
            // Do nothing
        }

        this.isPanning = false;
        this.canvas.style.cursor = this.isInConnectionMode ? 'crosshair' : 'grab';
    }

    /**
     * Handle mouse leave
     */
    handleMouseLeave() {
        this.isDragging = false;
        this.isPanning = false;
        this.draggedNode = null;
        this.hoveredNode = null;
        this.canvas.style.cursor = 'default';
        this.render();
    }

    /**
     * Handle mouse wheel (zoom)
     */
    handleWheel(e) {
        e.preventDefault();

        const rect = this.canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        // Zoom in/out
        const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
        const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale * zoomFactor));

        // Adjust viewport to zoom towards mouse position
        const worldBefore = this.screenToWorld(e.clientX, e.clientY);
        this.scale = newScale;
        const worldAfter = this.screenToWorld(e.clientX, e.clientY);

        this.viewportX += (worldAfter.x - worldBefore.x) * this.scale;
        this.viewportY += (worldAfter.y - worldBefore.y) * this.scale;

        this.render();
    }

    /**
     * Handle context menu (right click)
     */
    handleContextMenu(e) {
        e.preventDefault();

        // Check if clicking on a node first (nodes have priority over edges)
        const node = this.getNodeAtPosition(e.clientX, e.clientY);
        if (node && this.onNodeRightClick) {
            this.onNodeRightClick(node, e);
            return;
        }

        // If not on a node, check if clicking on an edge
        const edge = this.getEdgeAtPosition(e.clientX, e.clientY);
        if (edge && this.onEdgeRightClick) {
            this.onEdgeRightClick(edge, e);
        }
    }

    /**
     * Add a node to the graph
     */
    addNode(imageId, label = null, x = null, y = null, thumbnail = null) {
        // Check if node with this imageId already exists
        const existingNode = this.nodes.find(n => n.imageId === imageId);
        if (existingNode) {
            console.warn(`Node with imageId ${imageId} already exists, skipping...`);
            return existingNode;
        }

        const node = {
            id: this.nodes.length + 1,
            imageId: imageId,
            x: x !== null ? x : 0,
            y: y !== null ? y : 0,
            label: label || `Image ${imageId}`,
            thumbnail: thumbnail, // HTMLImageElement or null
            thumbnailLoaded: false,
            locked: x !== null && y !== null // If position provided, consider it locked
        };

        // Load thumbnail if provided as URL
        if (thumbnail && typeof thumbnail === 'string') {
            console.log(`[GraphManager] 🔄 Loading thumbnail for node ${node.id}:`, thumbnail);
            const img = new Image();
            img.crossOrigin = 'anonymous'; // Allow CORS
            img.onload = () => {
                console.log(`[GraphManager] ✅ Thumbnail loaded for node ${node.id}`);
                node.thumbnail = img;
                node.thumbnailLoaded = true;

                // Force a complete re-render
                this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
                this.render();
            };
            img.onerror = (error) => {
                console.error(`[GraphManager] ❌ Failed to load thumbnail for node ${node.id}:`, thumbnail);
                console.error('[GraphManager] Error details:', error);
                node.thumbnailLoaded = false;
                this.render(); // Re-render even on error
            };
            img.src = thumbnail;
        } else if (thumbnail instanceof HTMLImageElement) {
            node.thumbnailLoaded = true;
        }

        this.nodes.push(node);

        console.log(`[GraphManager] Node added: id=${node.id}, imageId=${node.imageId}, total nodes=${this.nodes.length}`);

        // If no position provided, run auto-layout
        if (x === null || y === null) {
            this.autoLayout();
        }

        this.render();

        if (this.onGraphChanged) {
            this.onGraphChanged();
        }

        return node;
    }

    /**
     * Set the selected image (to highlight in graph)
     */
    setSelectedImage(imageId) {
        this.selectedImageId = imageId;
        this.render();
    }

    /**
     * Remove a node from the graph
     */
    removeNode(nodeId) {
        // Remove node
        this.nodes = this.nodes.filter(n => n.id !== nodeId);

        // Remove connected edges
        this.edges = this.edges.filter(e => e.from !== nodeId && e.to !== nodeId);

        this.render();

        if (this.onGraphChanged) {
            this.onGraphChanged();
        }
    }

    /**
     * Update node label
     */
    updateNodeLabel(nodeId, newLabel) {
        const node = this.nodes.find(n => n.id === nodeId);
        if (node) {
            node.label = newLabel;
            this.render();

            if (this.onGraphChanged) {
                this.onGraphChanged();
            }
        }
    }

    /**
     * Add an edge between two nodes
     */
    addEdge(fromNodeId, toNodeId) {
        // Check if edge already exists
        const exists = this.edges.some(e =>
            (e.from === fromNodeId && e.to === toNodeId) ||
            (e.from === toNodeId && e.to === fromNodeId)
        );

        if (!exists) {
            this.edges.push({ from: fromNodeId, to: toNodeId });
            this.render();

            if (this.onGraphChanged) {
                this.onGraphChanged();
            }
        }
    }

    /**
     * Remove an edge between two nodes
     */
    removeEdge(fromNodeId, toNodeId) {
        const initialLength = this.edges.length;

        // Remove edge (bidirectional)
        this.edges = this.edges.filter(e =>
            !(e.from === fromNodeId && e.to === toNodeId) &&
            !(e.from === toNodeId && e.to === fromNodeId)
        );

        // If an edge was actually removed
        if (this.edges.length < initialLength) {
            console.log(`[GraphManager] Edge removed between nodes ${fromNodeId} and ${toNodeId}`);
            this.render();

            if (this.onGraphChanged) {
                this.onGraphChanged();
            }
        }
    }

    /**
     * Auto-layout algorithm - intelligent positioning based on existing nodes
     */
    autoLayout() {
        if (this.nodes.length === 0) return;

        // Get unlocked nodes that need positioning
        const unlockedNodes = this.nodes.filter(n => !n.locked);
        if (unlockedNodes.length === 0) return;

        // Get locked nodes (manually positioned)
        const lockedNodes = this.nodes.filter(n => n.locked);

        if (lockedNodes.length > 0) {
            // Smart positioning near existing nodes
            unlockedNodes.forEach(node => {
                // Find a good position near locked nodes
                const position = this.findBestPosition(lockedNodes);
                node.x = position.x;
                node.y = position.y;
            });
        } else {
            // No locked nodes - use grid layout
            const cols = Math.ceil(Math.sqrt(this.nodes.length));
            let col = 0;
            let row = 0;

            this.nodes.forEach((node) => {
                if (!node.locked) {
                    node.x = col * this.nodeSpacingX;
                    node.y = row * this.nodeSpacingY;

                    col++;
                    if (col >= cols) {
                        col = 0;
                        row++;
                    }
                }
            });
        }

        // Center viewport on graph if no locked nodes
        if (lockedNodes.length === 0) {
            this.centerView();
        }
    }

    /**
     * Find best position for a new node near existing locked nodes
     */
    findBestPosition(lockedNodes) {
        // Calculate center of locked nodes
        let centerX = 0;
        let centerY = 0;
        lockedNodes.forEach(node => {
            centerX += node.x;
            centerY += node.y;
        });
        centerX /= lockedNodes.length;
        centerY /= lockedNodes.length;

        // Try positions in a spiral around the center
        const directions = [
            { x: 1, y: 0 },   // Right
            { x: 0, y: 1 },   // Down
            { x: -1, y: 0 },  // Left
            { x: 0, y: -1 },  // Up
            { x: 1, y: 1 },   // Diagonal
            { x: -1, y: 1 },
            { x: -1, y: -1 },
            { x: 1, y: -1 }
        ];

        for (const dir of directions) {
            const testX = centerX + dir.x * this.nodeSpacingX;
            const testY = centerY + dir.y * this.nodeSpacingY;

            // Check if this position is free
            if (!this.isPositionOccupied(testX, testY)) {
                return { x: testX, y: testY };
            }
        }

        // Fallback: position to the right of the rightmost node
        const rightmost = lockedNodes.reduce((max, node) => node.x > max.x ? node : max, lockedNodes[0]);
        return { x: rightmost.x + this.nodeSpacingX, y: rightmost.y };
    }

    /**
     * Check if a position is already occupied by a node
     */
    isPositionOccupied(x, y, threshold = 50) {
        return this.nodes.some(node => {
            const dx = node.x - x;
            const dy = node.y - y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            return distance < threshold;
        });
    }

    /**
     * Center viewport on graph
     */
    centerView() {
        if (this.nodes.length === 0) {
            this.viewportX = this.canvas.width / 2;
            this.viewportY = this.canvas.height / 2;
            return;
        }

        // Calculate bounding box
        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;

        this.nodes.forEach(node => {
            minX = Math.min(minX, node.x);
            minY = Math.min(minY, node.y);
            maxX = Math.max(maxX, node.x);
            maxY = Math.max(maxY, node.y);
        });

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        this.viewportX = this.canvas.width / 2 - centerX * this.scale;
        this.viewportY = this.canvas.height / 2 - centerY * this.scale;
    }

    /**
     * Zoom in
     */
    zoomIn() {
        const centerX = this.canvas.width / 2;
        const centerY = this.canvas.height / 2;

        const worldBefore = this.screenToWorld(centerX, centerY);
        this.scale = Math.min(this.maxScale, this.scale * 1.2);
        const worldAfter = this.screenToWorld(centerX, centerY);

        this.viewportX += (worldAfter.x - worldBefore.x) * this.scale;
        this.viewportY += (worldAfter.y - worldBefore.y) * this.scale;

        this.render();
    }

    /**
     * Zoom out
     */
    zoomOut() {
        const centerX = this.canvas.width / 2;
        const centerY = this.canvas.height / 2;

        const worldBefore = this.screenToWorld(centerX, centerY);
        this.scale = Math.max(this.minScale, this.scale / 1.2);
        const worldAfter = this.screenToWorld(centerX, centerY);

        this.viewportX += (worldAfter.x - worldBefore.x) * this.scale;
        this.viewportY += (worldAfter.y - worldBefore.y) * this.scale;

        this.render();
    }

    /**
     * Reset view (center and reset zoom)
     */
    resetView() {
        this.scale = 1.0;
        this.centerView();
        this.render();
    }

    /**
     * Clear the graph
     */
    clear() {
        this.nodes = [];
        this.edges = [];
        this.render();
    }

    /**
     * Render the graph
     */
    render() {
        // Reset all transforms and clear canvas
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // Draw grid background (in screen space)
        this.drawGrid();

        // Save context for viewport transforms
        this.ctx.save();

        // Apply viewport transform
        this.ctx.translate(this.viewportX, this.viewportY);
        this.ctx.scale(this.scale, this.scale);

        // Draw edges first (behind nodes)
        this.drawEdges();

        // Draw nodes
        this.drawNodes();

        // Restore context (back to screen space)
        this.ctx.restore();
    }

    /**
     * Draw grid background
     */
    drawGrid() {
        const gridSize = 50 * this.scale;
        const offsetX = this.viewportX % gridSize;
        const offsetY = this.viewportY % gridSize;

        this.ctx.strokeStyle = '#1e293b';
        this.ctx.lineWidth = 1;

        // Vertical lines
        for (let x = offsetX; x < this.canvas.width; x += gridSize) {
            this.ctx.beginPath();
            this.ctx.moveTo(x, 0);
            this.ctx.lineTo(x, this.canvas.height);
            this.ctx.stroke();
        }

        // Horizontal lines
        for (let y = offsetY; y < this.canvas.height; y += gridSize) {
            this.ctx.beginPath();
            this.ctx.moveTo(0, y);
            this.ctx.lineTo(this.canvas.width, y);
            this.ctx.stroke();
        }
    }

    /**
     * Draw all edges
     */
    drawEdges() {
        this.edges.forEach(edge => {
            const fromNode = this.nodes.find(n => n.id === edge.from);
            const toNode = this.nodes.find(n => n.id === edge.to);

            if (fromNode && toNode) {
                // Check if either node is selected
                const isSelected = fromNode.imageId === this.selectedImageId || toNode.imageId === this.selectedImageId;
                const edgeColor = isSelected ? this.edgeSelectedColor : this.edgeColor;
                const lineWidth = (isSelected ? this.edgeWidth + 1 : this.edgeWidth) / this.scale;

                // Calculate edge points that stop at node boundaries
                const angle = Math.atan2(toNode.y - fromNode.y, toNode.x - fromNode.x);
                const nodeHalfSize = this.nodeSize / 2;

                // Start point (edge of fromNode)
                const startX = fromNode.x + Math.cos(angle) * nodeHalfSize;
                const startY = fromNode.y + Math.sin(angle) * nodeHalfSize;

                // End point (edge of toNode)
                const endX = toNode.x - Math.cos(angle) * nodeHalfSize;
                const endY = toNode.y - Math.sin(angle) * nodeHalfSize;

                // Draw edge with glow effect
                this.ctx.save();

                // Glow effect (shadow)
                this.ctx.shadowColor = edgeColor;
                this.ctx.shadowBlur = 8 / this.scale;
                this.ctx.strokeStyle = edgeColor;
                this.ctx.lineWidth = lineWidth;
                this.ctx.lineCap = 'round';

                this.ctx.beginPath();
                this.ctx.moveTo(startX, startY);
                this.ctx.lineTo(endX, endY);
                this.ctx.stroke();

                this.ctx.restore();
            }
        });
    }

    /**
     * Draw all nodes
     */
    drawNodes() {
        this.nodes.forEach(node => {
            const isHovered = this.hoveredNode === node;
            const isSelected = node.imageId === this.selectedImageId;
            const isSource = this.isInConnectionMode && node.id === this.connectionSourceNode?.id;
            const isAlreadyConnected = this.isInConnectionMode && this.isNodeConnectedToSource(node);
            const isSelf = this.isInConnectionMode && node.id === this.connectionSourceNode?.id;

            // Determine border color based on state
            let borderColor;
            if (isSource) {
                borderColor = '#f59e0b'; // Orange for source
            } else if (isAlreadyConnected || isSelf) {
                borderColor = '#64748b'; // Gray for unavailable
            } else if (isSelected) {
                borderColor = '#10b981'; // Green for selected
            } else if (isHovered && this.isInConnectionMode) {
                borderColor = '#8b5cf6'; // Purple for hoverable in connection mode
            } else if (isHovered) {
                borderColor = this.nodeBorderHoverColor;
            } else {
                borderColor = this.nodeBorderColor;
            }

            const borderWidth = isSource ? 6 / this.scale : (isSelected ? 5 / this.scale : (isHovered ? 4 / this.scale : 3 / this.scale));

            const halfSize = this.nodeSize / 2;

            // Save context for clipping
            this.ctx.save();

            // If thumbnail is loaded, draw it
            if (node.thumbnailLoaded && node.thumbnail) {
                // Create rounded rectangle clipping path
                const radius = 8 / this.scale;
                const x = node.x - halfSize;
                const y = node.y - halfSize;
                const width = this.nodeSize;
                const height = this.nodeSize;

                this.ctx.beginPath();
                this.ctx.moveTo(x + radius, y);
                this.ctx.lineTo(x + width - radius, y);
                this.ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
                this.ctx.lineTo(x + width, y + height - radius);
                this.ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
                this.ctx.lineTo(x + radius, y + height);
                this.ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
                this.ctx.lineTo(x, y + radius);
                this.ctx.quadraticCurveTo(x, y, x + radius, y);
                this.ctx.closePath();

                // Clip to rounded rectangle
                this.ctx.clip();

                // Draw thumbnail image
                this.ctx.drawImage(
                    node.thumbnail,
                    node.x - halfSize,
                    node.y - halfSize,
                    this.nodeSize,
                    this.nodeSize
                );

                this.ctx.restore();

                // Draw border around thumbnail
                this.ctx.save();
                this.ctx.beginPath();
                this.ctx.moveTo(x + radius, y);
                this.ctx.lineTo(x + width - radius, y);
                this.ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
                this.ctx.lineTo(x + width, y + height - radius);
                this.ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
                this.ctx.lineTo(x + radius, y + height);
                this.ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
                this.ctx.lineTo(x, y + radius);
                this.ctx.quadraticCurveTo(x, y, x + radius, y);
                this.ctx.closePath();

                this.ctx.strokeStyle = borderColor;
                this.ctx.lineWidth = borderWidth;
                this.ctx.stroke();

                // Add glow effect based on state
                if (isSource) {
                    this.ctx.shadowColor = 'rgba(245, 158, 11, 0.8)'; // Orange glow for source
                    this.ctx.shadowBlur = 20 / this.scale;
                    this.ctx.stroke();
                    this.ctx.shadowBlur = 0;
                } else if (isSelected) {
                    this.ctx.shadowColor = 'rgba(16, 185, 129, 0.8)';
                    this.ctx.shadowBlur = 15 / this.scale;
                    this.ctx.stroke();
                    this.ctx.shadowBlur = 0;
                } else if (isHovered && this.isInConnectionMode && !isAlreadyConnected && !isSelf) {
                    this.ctx.shadowColor = 'rgba(139, 92, 246, 0.8)'; // Purple glow for valid targets
                    this.ctx.shadowBlur = 15 / this.scale;
                    this.ctx.stroke();
                    this.ctx.shadowBlur = 0;
                } else if (isHovered) {
                    this.ctx.shadowColor = 'rgba(59, 130, 246, 0.5)';
                    this.ctx.shadowBlur = 10 / this.scale;
                    this.ctx.stroke();
                    this.ctx.shadowBlur = 0;
                }

                this.ctx.restore();
            } else {
                // Loading placeholder: show a loading state instead of fallback circle
                const x = node.x - halfSize;
                const y = node.y - halfSize;
                const width = this.nodeSize;
                const height = this.nodeSize;
                const radius = 8 / this.scale;

                // Draw rounded rectangle background
                this.ctx.save();
                this.ctx.beginPath();
                this.ctx.moveTo(x + radius, y);
                this.ctx.lineTo(x + width - radius, y);
                this.ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
                this.ctx.lineTo(x + width, y + height - radius);
                this.ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
                this.ctx.lineTo(x + radius, y + height);
                this.ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
                this.ctx.lineTo(x, y + radius);
                this.ctx.quadraticCurveTo(x, y, x + radius, y);
                this.ctx.closePath();

                // Dark background for loading state
                this.ctx.fillStyle = '#1e293b';
                this.ctx.fill();

                this.ctx.strokeStyle = borderColor;
                this.ctx.lineWidth = borderWidth;
                this.ctx.stroke();

                // Draw loading icon (simple spinner)
                this.ctx.strokeStyle = '#64748b';
                this.ctx.lineWidth = 3 / this.scale;
                this.ctx.beginPath();
                this.ctx.arc(node.x, node.y, 15 / this.scale, 0, Math.PI * 1.5);
                this.ctx.stroke();

                this.ctx.restore();
            }

            // Draw label below the node
            this.ctx.fillStyle = this.nodeLabelColor;
            this.ctx.font = `${this.nodeLabelFont}`;
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'top';

            // Truncate label if too long
            let label = node.label;
            const maxLength = 15;
            if (label.length > maxLength) {
                label = label.substring(0, maxLength - 3) + '...';
            }

            // Draw label with background for better readability
            const labelY = node.y + halfSize + 5 / this.scale;
            const metrics = this.ctx.measureText(label);
            const labelWidth = metrics.width;
            const padding = 4 / this.scale;

            this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
            this.ctx.fillRect(
                node.x - labelWidth / 2 - padding,
                labelY - padding,
                labelWidth + padding * 2,
                12 / this.scale + padding * 2
            );

            this.ctx.fillStyle = this.nodeLabelColor;
            this.ctx.fillText(label, node.x, labelY);
        });
    }

    /**
     * Get graph data for export
     */
    exportData() {
        return {
            nodes: this.nodes.map(n => ({
                imageId: n.imageId,
                x: n.x,
                y: n.y,
                locked: n.locked
            })),
            edges: this.edges
        };
    }

    /**
     * Load graph data
     */
    loadData(data) {
        this.nodes = data.nodes || [];
        this.edges = data.edges || [];
        this.render();
    }

    /**
     * Start connection creation mode
     * @param {Object} sourceNode - The source node (current image)
     */
    startConnectionMode(sourceNode) {
        this.isInConnectionMode = true;
        this.connectionSourceNode = sourceNode;
        this.canvas.style.cursor = 'crosshair';
        this.setHelpText('🎯 Sélectionnez l\'image de destination dans le graph (Échap pour annuler)');

        // Notify connection mode changed
        if (this.onConnectionModeChanged) {
            this.onConnectionModeChanged(true);
        }

        this.render();
        console.log('[GraphManager] Connection mode started from node:', sourceNode.label);
    }

    /**
     * Cancel connection creation mode
     */
    cancelConnectionMode() {
        this.isInConnectionMode = false;
        this.connectionSourceNode = null;
        this.canvas.style.cursor = 'grab';
        this.setHelpText(''); // Empty = show default help

        // Notify connection mode changed
        if (this.onConnectionModeChanged) {
            this.onConnectionModeChanged(false);
        }

        this.render();
        console.log('[GraphManager] Connection mode cancelled');
    }

    /**
     * Handle node click in connection mode
     * @param {Object} node - The clicked node
     */
    handleConnectionNodeClick(node) {
        // Can't connect to self
        if (node.id === this.connectionSourceNode.id) {
            this.showError('❌ Impossible de connecter une image à elle-même');
            return;
        }

        // Check if connection already exists
        const connectionExists = this.edges.some(edge =>
            (edge.from === this.connectionSourceNode.id && edge.to === node.id) ||
            (edge.from === node.id && edge.to === this.connectionSourceNode.id)
        );

        if (connectionExists) {
            this.showError('❌ Une connexion existe déjà entre ces images');
            return;
        }

        // Valid destination - notify callback
        if (this.onConnectionRequested) {
            this.onConnectionRequested(this.connectionSourceNode, node);
        }

        // Don't exit connection mode yet - it will be exited when placement is done or cancelled
        // The button stays in "Annuler" mode to allow canceling the placement
    }

    /**
     * Check if a node is already connected to the source
     * @param {Object} node - Node to check
     * @returns {boolean} True if already connected
     */
    isNodeConnectedToSource(node) {
        if (!this.connectionSourceNode) return false;

        return this.edges.some(edge =>
            (edge.from === this.connectionSourceNode.id && edge.to === node.id) ||
            (edge.from === node.id && edge.to === this.connectionSourceNode.id)
        );
    }

    /**
     * Set help text above the graph
     * @param {string} text - Help text to display (empty string to show default help)
     * @param {string} type - Type of message ('info', 'error')
     */
    setHelpText(text, type = 'info') {
        const helpTextEl = document.getElementById('graph-help-text');
        if (helpTextEl) {
            // Update style and content based on type
            if (type === 'error') {
                helpTextEl.className = 'text-sm text-center text-white bg-red-600 rounded-lg px-4 py-2.5 mb-2 min-h-[40px] flex items-center justify-center transition-all duration-200 font-medium shadow-lg';
                helpTextEl.innerHTML = text;
            } else if (text) {
                // Custom info message (blue background)
                helpTextEl.className = 'text-sm text-center text-white bg-blue-600 rounded-lg px-4 py-2.5 mb-2 min-h-[40px] flex items-center justify-center transition-all duration-200 font-medium shadow-lg';
                helpTextEl.innerHTML = text;
            } else {
                // Default help with icons (restored from original HTML)
                helpTextEl.className = 'text-xs text-slate-300 bg-slate-800/60 rounded-lg px-3 py-2 mb-2 min-h-[36px] flex items-center justify-center transition-all duration-200 border border-slate-700/50';
                helpTextEl.innerHTML = `
                    <span class="flex items-center gap-3">
                        <span class="flex items-center gap-1.5">
                            <svg class="w-3.5 h-3.5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"/></svg>
                            <span>Cliquez pour voir</span>
                        </span>
                        <span class="text-slate-600">|</span>
                        <span class="flex items-center gap-1.5">
                            <svg class="w-3.5 h-3.5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"/></svg>
                            <span>Glissez pour déplacer</span>
                        </span>
                        <span class="text-slate-600">|</span>
                        <span class="flex items-center gap-1.5">
                            <svg class="w-3.5 h-3.5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"/></svg>
                            <span>Clic droit pour options</span>
                        </span>
                    </span>
                `;
            }
        }
    }

    /**
     * Show error message in help text (auto-clears after delay)
     * @param {string} message - Error message to display
     */
    showError(message) {
        this.setHelpText(message, 'error');

        // Auto-clear after 3 seconds
        setTimeout(() => {
            if (this.isInConnectionMode) {
                this.setHelpText('🎯 Sélectionnez l\'image de destination dans le graph (Échap pour annuler)', 'info');
            } else {
                this.setHelpText(''); // Show default help
            }
        }, 3000);
    }

    /**
     * Check if current image has any available connections to create
     * @param {number} currentImageId - ID of the current image
     * @returns {boolean} True if connections are available
     */
    hasAvailableConnections(currentImageId) {
        const currentNode = this.nodes.find(n => n.imageId === currentImageId);
        if (!currentNode) return false;

        // Find all nodes that are not the current node
        const otherNodes = this.nodes.filter(n => n.id !== currentNode.id);

        // Check if any of them are not already connected
        return otherNodes.some(node => !this.isNodeConnectedToSource.call(
            { connectionSourceNode: currentNode, edges: this.edges },
            node
        ));
    }

    /**
     * Dispose of the GraphManager and clean up resources
     */
    dispose() {
        console.log('[GraphManager] Disposing...');

        // Remove window resize listener
        if (this.boundResizeHandler) {
            window.removeEventListener('resize', this.boundResizeHandler);
            this.boundResizeHandler = null;
        }

        // Remove canvas event listeners
        if (this.canvas) {
            if (this.boundMouseDownHandler) {
                this.canvas.removeEventListener('mousedown', this.boundMouseDownHandler);
            }
            if (this.boundMouseMoveHandler) {
                this.canvas.removeEventListener('mousemove', this.boundMouseMoveHandler);
            }
            if (this.boundMouseUpHandler) {
                this.canvas.removeEventListener('mouseup', this.boundMouseUpHandler);
            }
            if (this.boundMouseLeaveHandler) {
                this.canvas.removeEventListener('mouseleave', this.boundMouseLeaveHandler);
            }
            if (this.boundWheelHandler) {
                this.canvas.removeEventListener('wheel', this.boundWheelHandler);
            }
            if (this.boundContextMenuHandler) {
                this.canvas.removeEventListener('contextmenu', this.boundContextMenuHandler);
            }
        }

        // Clear bound handlers
        this.boundMouseDownHandler = null;
        this.boundMouseMoveHandler = null;
        this.boundMouseUpHandler = null;
        this.boundMouseLeaveHandler = null;
        this.boundWheelHandler = null;
        this.boundContextMenuHandler = null;

        // Clear canvas context
        if (this.ctx && this.canvas) {
            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        }

        // Clear data
        this.nodes = [];
        this.edges = [];
        this.hoveredNode = null;
        this.draggedNode = null;
        this.connectionSourceNode = null;

        // Clear callbacks
        this.onNodeClick = null;
        this.onNodeRightClick = null;
        this.onEdgeRightClick = null;
        this.onGraphChanged = null;
        this.onConnectionRequested = null;
        this.onConnectionModeChanged = null;

        console.log('[GraphManager] ✅ Disposed');
    }
}
